import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { BankService } from 'src/app/services/bank/bank.service';
import { BankModalService } from 'src/app/services/bank-modal/bank-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-md-bank',
  templateUrl: './add-edit-md-bank.component.html',
  styleUrls: ['./add-edit-md-bank.component.scss']
})
export class AddEditMdBankComponent extends DestructorComponent implements OnInit {

  @Output() bankAdded = new EventEmitter<void>();
  @Output() bankUpdated = new EventEmitter<void>();

  bankForm!: FormGroup;
  isLoading = false;
  isEditMode = false;
  selectedBank: any = null;

  constructor(
    private fb: FormBuilder,
    private _bankService: BankService,
    private _bankModalService: BankModalService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.bankForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      name_jpy: [''],
      branch_name: [''],
      branch_name_jpy: [''],
      branch_code: [''],
      branch_code_jpy: [''],
      account_name: [''],
      account_name_jpy: [''],
      account_number: [''],
      account_number_jpy: [''],
      swift_code: [''],
      swift_code_jpy: [''],
      address: [''],
      address_jpy: [''],
      beneficiary_address: [''],
      beneficiary_address_jpy: ['']
    });
  }

  // Getter methods for form controls
  get name() { return this.bankForm.get('name'); }
  get name_jpy() { return this.bankForm.get('name_jpy'); }
  get branch_name() { return this.bankForm.get('branch_name'); }
  get branch_name_jpy() { return this.bankForm.get('branch_name_jpy'); }
  get branch_code() { return this.bankForm.get('branch_code'); }
  get branch_code_jpy() { return this.bankForm.get('branch_code_jpy'); }
  get account_name() { return this.bankForm.get('account_name'); }
  get account_name_jpy() { return this.bankForm.get('account_name_jpy'); }
  get account_number() { return this.bankForm.get('account_number'); }
  get account_number_jpy() { return this.bankForm.get('account_number_jpy'); }
  get swift_code() { return this.bankForm.get('swift_code'); }
  get swift_code_jpy() { return this.bankForm.get('swift_code_jpy'); }
  get address() { return this.bankForm.get('address'); }
  get address_jpy() { return this.bankForm.get('address_jpy'); }
  get beneficiary_address() { return this.bankForm.get('beneficiary_address'); }
  get beneficiary_address_jpy() { return this.bankForm.get('beneficiary_address_jpy'); }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_bank_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceBank = this._bankModalService.getSelectedBank();
        if (serviceBank) {
          this.isEditMode = true;
          this.selectedBank = serviceBank;
          this.populateForm(this.selectedBank);
        } else {
          this.isEditMode = false;
          this.selectedBank = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._bankModalService.clearSelectedBank();
        this.resetForm();
      });
    }
  }

  private populateForm(bank: any): void {
    this.bankForm.patchValue({
      name: bank.name || '',
      name_jpy: bank.name_jpy || '',
      branch_name: bank.branch_name || '',
      branch_name_jpy: bank.branch_name_jpy || '',
      branch_code: bank.branch_code || '',
      branch_code_jpy: bank.branch_code_jpy || '',
      account_name: bank.account_name || '',
      account_name_jpy: bank.account_name_jpy || '',
      account_number: bank.account_number || '',
      account_number_jpy: bank.account_number_jpy || '',
      swift_code: bank.swift_code || '',
      swift_code_jpy: bank.swift_code_jpy || '',
      address: bank.address || '',
      address_jpy: bank.address_jpy || '',
      beneficiary_address: bank.beneficiary_address || '',
      beneficiary_address_jpy: bank.beneficiary_address_jpy || ''
    });
  }

  private resetForm(): void {
    this.bankForm.reset();
    this.isLoading = false;
    this.markFormGroupUntouched(this.bankForm);
  }

  private markFormGroupUntouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsUntouched();
      control?.markAsPristine();
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_bank_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  onSubmit(): void {
    if (this.bankForm.valid && !this.isLoading) {
      this.isLoading = true;

      const formData = this.bankForm.value;

      // Remove empty fields
      const submitData: any = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData[key] = formData[key];
        }
      });

      const apiCall = this.isEditMode
        ? this._bankService.UpdateData(this.selectedBank._id, submitData)
        : this._bankService.AddData(submitData);

      this.subscriptions.push(
        apiCall.subscribe({
          next: (response: any) => {
            this.isLoading = false;
            console.log('Bank operation successful:', response);

            // Show success message
            Swal.fire({
              text: this.isEditMode
                ? "Bank has been successfully updated!"
                : "Bank has been successfully created!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                // Close the modal
                this.closeModal();

                // Emit appropriate event
                if (this.isEditMode) {
                  this.bankUpdated.emit();
                } else {
                  this.bankAdded.emit();
                }

                // Reset the form
                this.resetForm();
              }
            });
          },
          error: (error: any) => {
            this.isLoading = false;
            console.error('Bank operation failed:', error);

            // Show error message
            Swal.fire({
              text: error.error?.message || "An error occurred. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    } else {
      this.markFormGroupTouched(this.bankForm);
    }
  }
}
